import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import FeedbackDrawer from './components/feedback/FeedbackDrawer';
import { useState, useEffect, createContext, useContext } from 'react';
import { debugCollector } from './utils/DebugCollector';
import LoginPage from './pages/Login';
import LeitstandBausteine from './pages/LeitstandBausteine';
import TwoFactorVerifyPage from './pages/TwoFactorVerify';
import TwoFactorSetupPage from './pages/TwoFactorSetup';
import StationScreen from './pages/StationScreen';
import HeuteBetrieb from './pages/HeuteBetrieb';
import EinrichtenKatalog from './pages/einrichten/EinrichtenKatalog';
import ObjektListe from './pages/einrichten/ObjektListe';
import Objektblatt from './pages/einrichten/Objektblatt';
import StreckeBlatt from './pages/einrichten/StreckeBlatt';
import BenutzerListe from './pages/einrichten/BenutzerListe';
import BenutzerBlatt from './pages/einrichten/BenutzerBlatt';
import KundenListe from './pages/einrichten/KundenListe';
import KundeBlatt from './pages/einrichten/KundeBlatt';
import GruppeBlatt from './pages/einrichten/GruppeBlatt';
import GeraetKoppeln from './pages/einrichten/GeraetKoppeln';
import StationenListe from './pages/einrichten/StationenListe';
import StationBlatt from './pages/einrichten/StationBlatt';
import WashPerformanceLeitstand from './pages/WashPerformanceLeitstand';
import WsaLeitstand from './pages/WsaLeitstand';
import ProcessingLeitstand from './pages/ProcessingLeitstand';
import TwaLeitstand from './pages/TwaLeitstand';
import DisruptionsLeitstand from './pages/DisruptionsLeitstand';
import CorrectionsLeitstand from './pages/CorrectionsLeitstand';
import ShiftsLeitstand from './pages/ShiftsLeitstand';
import AuditBetrieb from './pages/AuditBetrieb';
import DisruptionsBetrieb from './pages/DisruptionsBetrieb';
import DisruptionDetailPage from './pages/DisruptionDetail';
// PR-8a: Stammdaten-Seiten
// Schichten (vereinte Seite) + Detail-Seiten (Listen-Seiten redirecten auf /admin/shifts)
import ShiftsPage from './pages/admin/ShiftsPage';
import ShiftTypeDetailPage from './pages/admin/ShiftTypeDetailPage';
// PR-8d: Tech-Seiten
// Phase 3B: Mein Profil
import ProfilePage from './pages/ProfilePage';
import { getMe, MeResponse } from './api/client';
// PR-5X-C: Modul-System
import { ModulesProvider } from './hooks/useModules';
// PR-5X-E: System-Seiten
import ModuleFlagsPage from './pages/admin/ModuleFlagsPage';
// Wäscheklasse-Konfiguration
// Demo: Flow-Builder Prototyp
// Demo 2: Erfassungs-Flow Builder (vertikale Kette)
// Demo 3: Erfassungs-Flow Builder (Baum-Konzept)
// Demo 4: Erfassungs-Flow Builder (Folien-Muster, vgl. docs/CAPTURE-FLOW-BUILDER-KONZEPT.md)
// Station Pairing
import PairPage from './pages/PairPage';
// Password Reset + Invite + Email Verify
import ForgotPasswordPage from './pages/ForgotPassword';
import SetPasswordPage from './pages/SetPassword';
import VerifyEmailPage from './pages/VerifyEmail';
import FeedbackPage from './pages/admin/FeedbackPage';
import ChangelogPage from './pages/admin/ChangelogPage';
import GeraeteListe from './pages/einrichten/GeraeteListe';
import GeraetBlatt from './pages/einrichten/GeraetBlatt';

// =============================================================================
// AUTH CONTEXT
// =============================================================================

interface AuthContextType {
  user: MeResponse | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  refresh: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

// =============================================================================
// PROTECTED ROUTE (mit returnTo)
// =============================================================================

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user || user.stationOnly) {
    // returnTo = aktuelle URL (path + query)
    const returnTo = location.pathname + location.search;
    const loginUrl = `/login?returnTo=${encodeURIComponent(returnTo)}`;
    return <Navigate to={loginUrl} replace />;
  }

  return <>{children}</>;
}

// =============================================================================
// APP
// =============================================================================

export default function App() {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const data = await getMe();
      // Station-only Mode: Für UI-Zwecke als "kein User" behandeln
      // aber stationId bleibt verfügbar
      if (data.stationOnly) {
        console.info('[auth] Station-only mode, stationId:', data.stationId);
        setUser(data);
      } else {
        setUser(data);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    debugCollector.init();
    refresh();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-grund">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, loading, refresh }}>
      <ModulesProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/reset-password" element={<SetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/2fa/verify" element={<TwoFactorVerifyPage />} />
          <Route path="/2fa/setup" element={<TwoFactorSetupPage />} />
          <Route path="/pair" element={<PairPage />} />

          {/*
          Baustein-Katalog des Leitstands. Zeigt ausschliesslich Beispielwerte,
          liest nichts aus der Datenbank und ruft keine API — deshalb ohne
          Anmeldung erreichbar. Werkbank fuer den Umbau der Verwaltungsseiten.
        */}
          <Route path="/leitstand/bausteine" element={<LeitstandBausteine />} />

          {/* Station Routes (Station Cookie OR User Session) */}
          <Route path="/station" element={<StationScreen />} />
          <Route path="/wsa" element={<StationScreen />} />
          <Route path="/vorarbeiter" element={<StationScreen />} />
          <Route path="/fl" element={<StationScreen />} />

          {/* Protected Routes */}
          <Route
            path="/admin/heute"
            element={
              <ProtectedRoute>
                <HeuteBetrieb />
              </ProtectedRoute>
            }
          />
          <Route path="/admin" element={<Navigate to="/admin/heute" replace />} />
          {/* Einrichten-Neubau P2: Katalog → Liste → Objektblatt */}
          <Route
            path="/einrichten"
            element={
              <ProtectedRoute>
                <EinrichtenKatalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/:art"
            element={
              <ProtectedRoute>
                <ObjektListe />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/strecken/:id"
            element={
              <ProtectedRoute>
                <StreckeBlatt />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/benutzer"
            element={
              <ProtectedRoute>
                <BenutzerListe />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/kunden"
            element={
              <ProtectedRoute>
                <KundenListe />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/kunden/:id"
            element={
              <ProtectedRoute>
                <KundeBlatt />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/kundengruppen/:id"
            element={
              <ProtectedRoute>
                <GruppeBlatt />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/sammelposten/:id"
            element={
              <ProtectedRoute>
                <GruppeBlatt art="sammelposten" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/geraet-koppeln"
            element={
              <ProtectedRoute>
                <GeraetKoppeln />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/geraete"
            element={
              <ProtectedRoute>
                <GeraeteListe />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/geraete/:id"
            element={
              <ProtectedRoute>
                <GeraetBlatt />
              </ProtectedRoute>
            }
          />
          {/* Schichttypen und -regeln pflegt man unter Einrichten; der Kalender
            im Betrieb zeigt, was daraus entsteht. */}
          <Route path="/tech" element={<Navigate to="/admin/disruptions" replace />} />
          <Route path="/tech/disruptions" element={<Navigate to="/admin/disruptions" replace />} />
          <Route
            path="/einrichten/schichten"
            element={
              <ProtectedRoute>
                <ShiftsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shifts-klassisch"
            element={<Navigate to="/einrichten/schichten" replace />}
          />
          <Route
            path="/einrichten/stationen"
            element={
              <ProtectedRoute>
                <StationenListe />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/stationen/:id"
            element={
              <ProtectedRoute>
                <StationBlatt />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/benutzer/:id"
            element={
              <ProtectedRoute>
                <BenutzerBlatt />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einrichten/:art/:id"
            element={
              <ProtectedRoute>
                <Objektblatt />
              </ProtectedRoute>
            }
          />
          <Route path="/dashboard" element={<Navigate to="/admin/heute" replace />} />
          <Route
            path="/reports/wash-performance"
            element={
              <ProtectedRoute>
                <WashPerformanceLeitstand />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/wash-performance-klassisch"
            element={<Navigate to="/reports/wash-performance" replace />}
          />
          <Route path="/admin/master-data" element={<Navigate to="/einrichten" replace />} />
          <Route
            path="/reports/processing"
            element={
              <ProtectedRoute>
                <ProcessingLeitstand />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/processing-klassisch"
            element={<Navigate to="/reports/processing" replace />}
          />
          <Route
            path="/reports/wsa"
            element={
              <ProtectedRoute>
                <WsaLeitstand />
              </ProtectedRoute>
            }
          />
          <Route path="/reports/wsa-klassisch" element={<Navigate to="/reports/wsa" replace />} />
          <Route
            path="/reports/twa"
            element={
              <ProtectedRoute>
                <TwaLeitstand />
              </ProtectedRoute>
            }
          />
          <Route path="/reports/twa-klassisch" element={<Navigate to="/reports/twa" replace />} />
          <Route
            path="/reports/disruptions"
            element={
              <ProtectedRoute>
                <DisruptionsLeitstand />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/disruptions-klassisch"
            element={<Navigate to="/reports/disruptions" replace />}
          />
          <Route
            path="/admin/corrections"
            element={
              <ProtectedRoute>
                <CorrectionsLeitstand />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <ProtectedRoute>
                <AuditBetrieb />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/disruptions"
            element={
              <ProtectedRoute>
                <DisruptionsBetrieb />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/disruptions/:id"
            element={
              <ProtectedRoute>
                <DisruptionDetailPage />
              </ProtectedRoute>
            }
          />

          {/* PR-8a: Stammdaten Routes */}
          <Route path="/admin/customers" element={<Navigate to="/einrichten/kunden" replace />} />
          <Route
            path="/admin/customers/:id"
            element={<Navigate to="/einrichten/kunden" replace />}
          />
          <Route
            path="/admin/customer-groups"
            element={<Navigate to="/einrichten/kundengruppen" replace />}
          />
          <Route
            path="/admin/customer-groups/:id"
            element={<Navigate to="/einrichten/kundengruppen" replace />}
          />
          <Route
            path="/admin/system-buckets"
            element={<Navigate to="/einrichten/kundengruppen" replace />}
          />
          <Route
            path="/admin/system-buckets/:id"
            element={<Navigate to="/einrichten/kundengruppen" replace />}
          />
          <Route path="/admin/machines" element={<Navigate to="/einrichten/maschinen" replace />} />
          <Route
            path="/admin/machines/:id"
            element={<Navigate to="/einrichten/maschinen" replace />}
          />
          <Route
            path="/admin/machine-types"
            element={<Navigate to="/einrichten/maschinentypen" replace />}
          />
          <Route
            path="/admin/machine-types/:id"
            element={<Navigate to="/einrichten/maschinentypen" replace />}
          />
          <Route path="/admin/programs" element={<Navigate to="/einrichten/programme" replace />} />
          <Route
            path="/admin/programs/:id"
            element={<Navigate to="/einrichten/programme" replace />}
          />
          <Route
            path="/admin/program-types"
            element={<Navigate to="/einrichten/programme" replace />}
          />
          <Route
            path="/admin/program-types/:id"
            element={<Navigate to="/einrichten/programme" replace />}
          />
          <Route
            path="/admin/laundry-classes"
            element={<Navigate to="/einrichten/waescheklassen" replace />}
          />
          <Route
            path="/admin/laundry-classes/:id"
            element={<Navigate to="/einrichten/waescheklassen" replace />}
          />
          <Route
            path="/admin/article-groups"
            element={<Navigate to="/einrichten/artikelgruppen" replace />}
          />
          <Route
            path="/admin/article-groups/:id"
            element={<Navigate to="/einrichten/artikelgruppen" replace />}
          />
          {/* Demo: Flow-Builder Prototyp */}
          {/* Demo 2: Erfassungs-Flow Builder (vertikale Kette) */}
          {/* Demo 3: Erfassungs-Flow Builder (Baum-Konzept) */}
          {/* Demo 5: Erfassungs-Flow Builder (Wegekarte, Leitstand-Entwurf) */}
          <Route
            path="/admin/disruption-categories"
            element={<Navigate to="/einrichten/stoerungskategorien" replace />}
          />
          <Route
            path="/admin/disruption-categories/:id"
            element={<Navigate to="/einrichten/stoerungskategorien" replace />}
          />
          <Route path="/admin/lines" element={<Navigate to="/einrichten/strecken" replace />} />
          <Route path="/admin/lines/:id" element={<Navigate to="/einrichten/strecken" replace />} />
          <Route
            path="/admin/sublines/:id"
            element={<Navigate to="/einrichten/strecken" replace />}
          />
          {/* Schichten (vereinte Seite) + Detail-Seiten */}
          <Route
            path="/admin/shifts"
            element={
              <ProtectedRoute>
                <ShiftsLeitstand />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shift-types"
            element={<Navigate to="/admin/shifts?focus=types" replace />}
          />
          <Route
            path="/admin/shift-types/:id"
            element={
              <ProtectedRoute>
                <ShiftTypeDetailPage />
              </ProtectedRoute>
            }
          />
          {/* Die Schichtregeln sind entfallen (N6) — geplant wird im Kalender.
            Alte Lesezeichen landen dort statt im Nichts. */}
          <Route path="/admin/shift-rules" element={<Navigate to="/admin/shifts" replace />} />
          <Route path="/admin/shift-rules/:id" element={<Navigate to="/admin/shifts" replace />} />
          <Route path="/admin/stations" element={<Navigate to="/einrichten/stationen" replace />} />
          <Route
            path="/admin/stations/:id"
            element={<Navigate to="/einrichten/stationen" replace />}
          />
          <Route
            path="/admin/pair"
            element={<Navigate to="/einrichten/geraet-koppeln" replace />}
          />
          <Route
            path="/admin/ingest-devices"
            element={<Navigate to="/einrichten/geraete" replace />}
          />
          <Route
            path="/admin/ingest-devices/:id"
            element={<Navigate to="/einrichten/geraete" replace />}
          />
          <Route path="/admin/users" element={<Navigate to="/einrichten/benutzer" replace />} />
          <Route path="/admin/users/:id" element={<Navigate to="/einrichten/benutzer" replace />} />

          {/* Feedback System */}
          <Route
            path="/admin/feedback"
            element={
              <ProtectedRoute>
                <FeedbackPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/changelog"
            element={
              <ProtectedRoute>
                <ChangelogPage />
              </ProtectedRoute>
            }
          />

          {/* PR-5X-E: System Routes */}
          <Route
            path="/admin/system/module-flags"
            element={
              <ProtectedRoute>
                <ModuleFlagsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/capture-config"
            element={<Navigate to="/einrichten/strecken" replace />}
          />
          <Route
            path="/admin/capture-config/:id"
            element={<Navigate to="/einrichten/strecken" replace />}
          />

          {/* Phase 3B: Mein Profil */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* Default */}
          <Route
            path="/"
            element={<Navigate to={user && !user.stationOnly ? '/dashboard' : '/login'} replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {user && !user.stationOnly && <FeedbackDrawer />}
      </ModulesProvider>
    </AuthContext.Provider>
  );
}
